import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { IBmsLog } from '../models/bms';
import { IEmsLog } from '../models/ems';
import {
  IEnergyMeterHistoryLog,
  IEnergyMeterVoltageLog,
} from '../models/energy-meter';
import { IInverterLog } from '../models/inverter';
import { IHourlyEnergyPrice } from '../models/price';
import { Functions } from './shared/functions';

@Injectable({
  providedIn: 'root',
})
export class Log {
  private baseUrl: string = `${environment.apiUrl}/log`;
  private http: HttpClient = inject(HttpClient);
  private customFunctions: Functions = inject(Functions);

  // logStates = signal<any[]>([]);

  // Map of ID -> signal state
  emPowerLogState = signal(new Map<string, IEnergyMeterHistoryLog>());
  emPdqLogState = signal(new Map<string, IEnergyMeterHistoryLog>());
  emVoltageLogState = signal(new Map<string, IEnergyMeterVoltageLog>());
  emsLogState = signal(new Map<string, IEmsLog>());
  bmsLogState = signal(new Map<string, IBmsLog>());
  inverterLogState = signal(new Map<string, IInverterLog>());
  priceLogState = signal(new Map<string, IHourlyEnergyPrice>());

  private allLogStates = [
    this.emPowerLogState,
    this.emPdqLogState,
    this.emVoltageLogState,
    this.emsLogState,
    this.bmsLogState,
    this.inverterLogState,
    this.priceLogState,
  ];

  allLogsLoaded = computed(() => {
    const isMapLoaded = (map: Map<string, any>) => {
      if (map.size === 0) return true; // skip maps that were never requested
      return Array.from(map.values()).every((entry) => !entry.isLoading);
    };

    return this.allLogStates.every((signalState) => isMapLoaded(signalState()));
  });

  /** Get a signal for one ID */
  getEmPowerLogState(id: string) {
    return computed(() => this.emPowerLogState().get(id));
  }
  getEmPdqLogState(id: string) {
    return computed(() => this.emPdqLogState().get(id));
  }
  getEmVoltageLogState(id: string) {
    return computed(() => this.emVoltageLogState().get(id));
  }
  getEmsLogState(id: string) {
    return computed(() => this.emsLogState().get(id));
  }
  getBmsLogState(id: string) {
    return computed(() => this.bmsLogState().get(id));
  }
  getInverterLogState(id: string) {
    return computed(() => this.inverterLogState().get(id));
  }
  getPriceLogState(id: string) {
    return computed(() => this.priceLogState().get(id));
  }

  getEmPowerLog(Id: number, date: string): void {
    const current = new Map(this.emPowerLogState());
    current.set(Id.toString(), {
      ID: Id,
      lastLogID: 0,
      deviceNo: 0,
      data: [],
      isLoading: true,
    });
    this.emPowerLogState.set(current);

    this.http
      .get<IEnergyMeterHistoryLog>(`${this.baseUrl}/em/${Id}/${date}`)
      .subscribe({
        next: (data) => {
          const updated = new Map(this.emPowerLogState());

          if (data) {
            const allDates = this.create24HArray(data.data, date);

            updated.set(Id.toString(), {
              ID: data.ID,
              lastLogID: data.lastLogID,
              deviceNo: data.deviceNo,
              data: allDates,
              isLoading: false,
            });
          } else {
            updated.set(Id.toString(), {
              ID: Id,
              lastLogID: 0,
              deviceNo: 0,
              data: [],
              isLoading: false,
              error: 'Failed to load',
            });
          }
          this.emPowerLogState.set(updated);
        },
        error: (err) => {
          const updated = new Map(this.emPowerLogState());
          updated.set(Id.toString(), {
            ID: Id,
            lastLogID: 0,
            deviceNo: 0,
            data: [],
            isLoading: false,
            error: 'Failed to load',
          });
          this.emPowerLogState.set(updated);
        },
      });
  }

  getEmPdqLog(Id: number, date: string): void {
    const current = new Map(this.emPdqLogState());
    current.set(Id.toString(), {
      ID: Id,
      lastLogID: 0,
      deviceNo: 0,
      data: [],
      isLoading: true,
    });
    this.emPdqLogState.set(current);

    this.http
      .get<IEnergyMeterHistoryLog>(`${this.baseUrl}/empdq/${Id}/${date}`)
      .subscribe({
        next: (data) => {
          const updated = new Map(this.emPdqLogState());

          if (data) {
            const allDates = this.create24HArray(data.data, date);
            updated.set(Id.toString(), {
              ID: data.ID,
              lastLogID: data.lastLogID,
              deviceNo: data.deviceNo,
              data: allDates,
              isLoading: false,
            });
          } else {
            updated.set(Id.toString(), {
              ID: Id,
              lastLogID: 0,
              data: [],
              deviceNo: 0,
              isLoading: false,
              error: 'Failed to load',
            });
          }
          this.emPdqLogState.set(updated);
        },
        error: (err) => {
          const updated = new Map(this.emPdqLogState());
          updated.set(Id.toString(), {
            ID: Id,
            lastLogID: 0,
            data: [],
            isLoading: false,
            deviceNo: 0,
            error: 'Failed to load',
          });
          this.emPdqLogState.set(updated);
        },
      });
  }

  getEmVoltageLog(Id: number, date: string): void {
    const current = new Map(this.emVoltageLogState());
    current.set(Id.toString(), {
      ID: Id,
      lastLogID: 0,
      deviceNo: 0,
      data: [],
      isLoading: true,
    });
    this.emVoltageLogState.set(current);

    this.http
      .get<IEnergyMeterVoltageLog>(`${this.baseUrl}/emvoltage/${Id}/${date}`)
      .subscribe({
        next: (data) => {
          const updated = new Map(this.emVoltageLogState());

          if (data) {
            const allDates = this.create24HArray(data.data, date);
            updated.set(Id.toString(), {
              ID: data.ID,
              lastLogID: data.lastLogID,
              data: allDates,
              deviceNo: data.deviceNo,
              isLoading: false,
            });
          } else {
            updated.set(Id.toString(), {
              ID: Id,
              lastLogID: 0,
              data: [],
              deviceNo: 0,
              isLoading: false,
              error: 'Failed to load',
            });
          }
          this.emVoltageLogState.set(updated);
        },
        error: (err) => {
          const updated = new Map(this.emVoltageLogState());
          updated.set(Id.toString(), {
            ID: Id,
            lastLogID: 0,
            data: [],
            isLoading: false,
            deviceNo: 0,
            error: 'Failed to load',
          });
          this.emVoltageLogState.set(updated);
        },
      });
  }

  getEmsLog(Id: number, date: string): void {
    const current = new Map(this.emsLogState());
    current.set(Id.toString(), {
      ID: Id,
      lastLogID: 0,
      data: [],
      isLoading: true,
      deviceNo: 0,
    });
    this.emsLogState.set(current);

    this.http.get<IEmsLog>(`${this.baseUrl}/ems/${Id}/${date}`).subscribe({
      next: (data) => {
        const updated = new Map(this.emsLogState());
        if (data) {
          const allDates = this.create24HArray(data.data, date);
          updated.set(Id.toString(), {
            ID: data.ID,
            lastLogID: data.lastLogID,
            data: allDates,
            isLoading: false,
            deviceNo: data.deviceNo,
          });
        } else {
          updated.set(Id.toString(), {
            ID: Id,
            lastLogID: 0,
            data: [],
            isLoading: false,
            deviceNo: 0,
            error: 'Failed to load',
          });
        }
        this.emsLogState.set(updated);
      },
      error: (err) => {
        const updated = new Map(this.emsLogState());
        updated.set(Id.toString(), {
          ID: Id,
          lastLogID: 0,
          data: [],
          isLoading: false,
          error: 'Failed to load',
          deviceNo: 0,
        });
        this.emsLogState.set(updated);
      },
    });
  }

  getBmsLog(Id: number, date: string): void {
    const current = new Map(this.bmsLogState());
    current.set(Id.toString(), {
      ID: Id,
      lastLogID: 0,
      data: [],
      deviceNo: 0,
      isLoading: true,
    });
    this.bmsLogState.set(current);

    this.http.get<IBmsLog>(`${this.baseUrl}/bms/${Id}/${date}`).subscribe({
      next: (data) => {
        const updated = new Map(this.bmsLogState());
        if (data === null) {
          updated.set(Id.toString(), {
            ID: Id,
            lastLogID: 0,
            data: [],
            isLoading: false,
            deviceNo: 0,
            error: 'Failed to load',
          });
        } else {
          const allDates = this.create24HArray(data.data, date);
          updated.set(Id.toString(), {
            ID: data.ID,
            lastLogID: data.lastLogID,
            deviceNo: data.deviceNo,
            data: allDates,
            isLoading: false,
          });
        }
        this.bmsLogState.set(updated);
      },
      error: (err) => {
        const updated = new Map(this.bmsLogState());
        updated.set(Id.toString(), {
          ID: Id,
          lastLogID: 0,
          data: [],
          isLoading: false,
          error: 'Failed to load',
          deviceNo: 0,
        });
        this.bmsLogState.set(updated);
      },
    });
  }

  getInverterLog(Id: number, date: string): void {
    const current = new Map(this.inverterLogState());
    current.set(Id.toString(), {
      ID: Id,
      lastLogID: 0,
      data: [],
      deviceNo: 0,
      isLoading: true,
    });
    this.inverterLogState.set(current);

    this.http
      .get<IInverterLog>(`${this.baseUrl}/inverter/${Id}/${date}`)
      .subscribe({
        next: (data) => {
          const updated = new Map(this.inverterLogState());
          if (data) {
            const allDates = this.create24HArray(data.data, date);
            updated.set(Id.toString(), {
              ID: data.ID,
              lastLogID: data.lastLogID,
              data: allDates,
              deviceNo: data.deviceNo,
              isLoading: false,
            });
          } else {
            updated.set(Id.toString(), {
              ID: Id,
              lastLogID: 0,
              data: [],
              isLoading: false,
              deviceNo: 0,
              error: 'Failed to load',
            });
          }
          this.inverterLogState.set(updated);
        },
        error: (err) => {
          const updated = new Map(this.inverterLogState());
          updated.set(Id.toString(), {
            ID: Id,
            lastLogID: 0,
            data: [],
            isLoading: false,
            error: 'Failed to load',
            deviceNo: 0,
          });
          this.inverterLogState.set(updated);
        },
      });
  }

  // getPriceLog(Id: number, date: string): void {
  //   this.isLoading.set(true);
  //   this.errorMessage.set('');

  //   this.http
  //     .get<IHourlyEnergyPrice[]>('mockup/controller_1_log_price.json')
  //     .subscribe({
  //       next: (data) => {
  //         this.priceLog.set(data);
  //         this.isLoading.set(false);
  //       },
  //       error: (err) => {
  //         console.error(err);
  //         this.errorMessage.set('Failed to load BMSLog.');
  //         this.isLoading.set(false);
  //       },
  //     });
  // }

  // allLogsLoaded = computed(() => {
  //   const isMapLoaded = (map: Map<string, any>) => {
  //     // if (map.size === 0) return false; // consider empty map as "not yet loaded"
  //     return Array.from(map.values()).every((entry) => !entry.isLoading);
  //   };

  //   return this.allLogStates.every((signalState) => isMapLoaded(signalState()));
  // });

  private create24HArray(data: any[], date: string): any[] {
    const allDates = this.customFunctions.generateTimestampsForWholeDay(date);

    allDates.forEach((date, i) => {
      data.find((o) => {
        if (new Date(o.x).toISOString() === date.x) {
          allDates[i] = o;
          return true;
        }
        return false;
      });
    });

    return allDates;
  }

  private createEmptyArray() {}
}
